import type { ReactNode } from 'react';
import { Link } from 'wouter';
import { RequireAuth } from '../auth/RequireAuth';
import { useAdminStatus } from '../../lib/admin/useAdminStatus';
import { routes } from '../../lib/assessment/routes';

/**
 * The administrative route guard (A4a).
 *
 * **Composed from `RequireAuth`, not written alongside it.** Administration is
 * authentication plus one further question, so the authentication half is
 * delegated entirely — a second copy of the signed-out, loading, and
 * password-recovery branches is a second place for them to be got wrong, and
 * getting them wrong here costs more than anywhere else in the application.
 *
 * The inner half never renders until `RequireAuth` is satisfied, so the admin
 * check is only ever made with a genuinely signed-in session.
 *
 * **This is a courtesy, not the boundary.** The real control is server-side:
 * `admin_users` and `admin_memberships` are revoked from `anon` and
 * `authenticated` with no policies, so the browser cannot read them at all and
 * cannot decide this for itself. Anything gated here is gated again by the
 * server. Removing this component would make the console ugly, not insecure.
 *
 * **Fails closed.** `checking` shows nothing, an error denies, and a refusal
 * denies. Only an affirmative answer from the server renders the children.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  return (
    <RequireAuth>
      <AdminGate>{children}</AdminGate>
    </RequireAuth>
  );
}

function AdminGate({ children }: { children: ReactNode }) {
  const status = useAdminStatus();

  // Consistent with `RequireAuth`, which renders nothing while the session is
  // resolving: a placeholder here would flash on every arrival, and flashing
  // "Access denied" at an administrator before the answer arrives is worse than
  // showing nothing for the same moment.
  if (status === 'checking') return null;

  if (status === 'admin') return <>{children}</>;

  return (
    <section className="flex flex-col gap-4" data-testid="admin-denied">
      <h1 className="text-2xl font-semibold">Access denied</h1>
      <p className="text-muted-foreground">
        {status === 'error'
          ? 'Your access could not be verified. If you are an administrator, try again in a moment.'
          : 'This area is for administrators. Your account does not have access.'}
      </p>
      {/* Requirement 2: a denied visitor must have somewhere to go. Without it
          this page is a dead end reachable from the navigation. */}
      <p>
        <Link
          href={routes.dashboard}
          data-testid="admin-denied-return"
          className="text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          Return to your dashboard
        </Link>
      </p>
    </section>
  );
}
